import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../hooks/useCart';
import { api } from '../services/api';
import { Product } from '../types';
import { QrPayment } from '../components/QrPayment';

import {
  Search,
  Plus,
  Minus,
  Trash2,
  CreditCard,
  Wifi,
  BatteryFull,
  ChevronRight,
  ChevronLeft,
  Coffee,
  Croissant as BakeryIcon,
  Utensils,
  ShoppingBag,
  ShoppingCart,
  Bell,
  Settings,
  LogOut,
} from 'lucide-react';

function getCategoryIcon(category: string) {
  const lc = category.toLowerCase();
  if (lc.includes('coffee') || lc.includes('drink') || lc.includes('beverage') || lc.includes('tea')) return Coffee;
  if (lc.includes('food') || lc.includes('fresh') || lc.includes('meal') || lc.includes('salad')) return Utensils;
  if (lc.includes('bakery') || lc.includes('bread') || lc.includes('pastry') || lc.includes('bake')) return BakeryIcon;
  return ShoppingBag;
}

function formatCurrency(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

export function POS() {
  const { user, organization, role, logout } = useAuth();
  const navigate = useNavigate();
  const cart = useCart();

  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!userMenuOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [userMenuOpen]);

  const handleLogout = () => {
    setUserMenuOpen(false);
    logout();
    navigate('/login');
  };

  const [products, setProducts] = useState<Product[]>([]);
  const [categoryOrder, setCategoryOrder] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('All Items');
  const [showQrPayment, setShowQrPayment] = useState(false);
  const [mobileCartOpen, setMobileCartOpen] = useState(false);

  useEffect(() => {
    api.getProducts().then(({ products, categoryOrder }) => {
      setProducts(products.filter(p => p.is_active));
      setCategoryOrder(categoryOrder || []);
    }).catch(err => setError(err.message)).finally(() => setLoading(false));
  }, []);

  const orderedCategoryNames = useMemo(() => {
    const present = new Set<string>();
    products.forEach(p => { if (p.category) present.add(p.category); });
    const known = categoryOrder.filter(c => present.has(c));
    const knownSet = new Set(known);
    const rest = Array.from(present).filter(c => !knownSet.has(c)).sort((a, b) => a.localeCompare(b));
    return [...known, ...rest];
  }, [products, categoryOrder]);

  const categoryRank = useMemo(() => {
    const map = new Map<string, number>();
    orderedCategoryNames.forEach((c, i) => map.set(c, i));
    return map;
  }, [orderedCategoryNames]);

  const categories = useMemo(() => {
    return [
      { id: 'All Items', name: 'All Items', icon: ShoppingBag },
      ...orderedCategoryNames.map(c => ({
        id: c,
        name: c,
        icon: getCategoryIcon(c)
      }))
    ];
  }, [orderedCategoryNames]);

  const filteredProducts = useMemo(() => {
    const matched = products.filter(p => {
      const matchesCategory = activeCategory === 'All Items' || p.category === activeCategory;
      const matchesSearch = p.name.toLowerCase().includes(searchQuery.toLowerCase()) || (p.category || '').toLowerCase().includes(searchQuery.toLowerCase());
      return matchesCategory && matchesSearch;
    });
    const rank = (cat?: string | null) => {
      if (!cat) return Number.MAX_SAFE_INTEGER;
      const r = categoryRank.get(cat);
      return r === undefined ? Number.MAX_SAFE_INTEGER : r;
    };
    return matched.slice().sort((a, b) => {
      const ra = rank(a.category);
      const rb = rank(b.category);
      if (ra !== rb) return ra - rb;
      return a.name.localeCompare(b.name);
    });
  }, [activeCategory, searchQuery, products, categoryRank]);

  const totalItems = useMemo(
    () => cart.items.reduce((sum, item) => sum + item.quantity, 0),
    [cart.items]
  );

  const handleCheckout = () => {
    if (cart.items.length === 0) return;
    setError('');
    setShowQrPayment(true);
  };

  const handleQrPaymentSuccess = () => {
    cart.clearCart();
    setShowQrPayment(false);
    setMobileCartOpen(false);
  };

  const currentDate = new Date().toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  const currentTime = new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });

  const initials = user?.fullName?.split(' ').map(w => w[0]).slice(0,2).join('') || '?';
  const displayName = user?.fullName?.split(' ')[0] + ' ' + (user?.fullName?.split(' ').slice(-1)[0]?.[0] || '') + '.';

  const canAccessSettings = role === 'owner' || role === 'manager';

  if (loading) {
    return <div className="h-screen w-full flex items-center justify-center text-slate-500 text-lg bg-[#f0f4f8]">Loading...</div>;
  }

  const cartItemsList = (
    <>
      {cart.items.length === 0 ? (
        <div className="h-full flex flex-col items-center justify-center text-slate-400 py-12">
          <ShoppingBag className="w-16 h-16 mb-4 opacity-20" />
          <p className="text-xl font-medium text-slate-500">Cart is empty</p>
          <p className="text-sm mt-2 opacity-70">Tap products to add them</p>
        </div>
      ) : (
        cart.items.map(item => {
          const product = products.find(p => p.id === item.product_id);
          return (
            <div key={item.product_id} className="flex gap-4 items-center bg-white p-3 pr-4 rounded-2xl shadow-sm border border-slate-100">
              <div className="w-16 h-16 md:w-20 md:h-20 rounded-xl overflow-hidden bg-slate-100 flex-shrink-0 relative">
                {product?.image_url ? (
                  <img src={product.image_url} alt={item.name} className="w-full h-full object-cover absolute inset-0" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-2xl font-bold text-[#2563eb] opacity-30 absolute inset-0 bg-slate-200/50">
                    {item.name.charAt(0)}
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="font-semibold text-slate-800 text-base md:text-lg leading-tight truncate">{item.name}</h4>
                <p className="text-[#2563eb] font-bold mt-1">{formatCurrency(item.price * item.quantity)}</p>
              </div>
              <div className="flex items-center gap-1 md:gap-2 bg-slate-50 p-1 rounded-xl border border-slate-100">
                <button
                  onClick={() => cart.updateQuantity(item.product_id, item.quantity - 1)}
                  className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-lg bg-white text-slate-600 shadow-sm border border-slate-200 active:scale-95 transition-all"
                >
                  <Minus className="w-4 h-4" />
                </button>
                <span className="w-6 text-center font-bold text-slate-800 text-lg">{item.quantity}</span>
                <button
                  onClick={() => cart.updateQuantity(item.product_id, item.quantity + 1)}
                  className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-lg bg-white text-slate-600 shadow-sm border border-slate-200 active:scale-95 transition-all"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            </div>
          );
        })
      )}
    </>
  );

  const totalsAndCheckout = (
    <>
      <div className="space-y-3 mb-6">
        <div className="flex justify-between text-slate-500 text-base md:text-lg font-medium">
          <span>Subtotal</span>
          <span>{formatCurrency(cart.totals.subtotal)}</span>
        </div>
        <div className="flex justify-between text-slate-500 text-base md:text-lg font-medium">
          <span>Tax</span>
          <span>{formatCurrency(cart.totals.tax)}</span>
        </div>
        {cart.totals.convenienceFee > 0 && (
          <div className="flex justify-between text-slate-500 text-base md:text-lg font-medium">
            <span>Convenience Fee (3%)</span>
            <span>{formatCurrency(cart.totals.convenienceFee)}</span>
          </div>
        )}
        <div className="border-t border-dashed border-slate-200 pt-3 flex justify-between text-xl md:text-2xl font-bold text-slate-900">
          <span>Total</span>
          <span>{formatCurrency(cart.totals.total)}</span>
        </div>
      </div>

      <button
        onClick={handleCheckout}
        disabled={cart.items.length === 0}
        className={`
          w-full h-16 md:h-[72px] rounded-2xl flex items-center justify-between px-6 md:px-8 text-lg md:text-xl font-semibold transition-all
          ${cart.items.length === 0
            ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
            : 'bg-[#2563eb] text-white shadow-xl shadow-blue-900/20 hover:bg-[#1d4ed8] active:scale-[0.98]'}
        `}
      >
        <div className="flex items-center gap-3">
          <CreditCard className={`w-6 h-6 md:w-7 md:h-7 ${cart.items.length === 0 ? 'opacity-50' : ''}`} />
          <span>Charge by QR</span>
        </div>
        <span>{formatCurrency(cart.totals.total)}</span>
      </button>
    </>
  );

  return (
    <div className="h-screen w-full bg-[#f0f4f8] flex flex-col md:flex-row font-sans text-slate-800 overflow-hidden selection:bg-blue-200">

      {/* --- MAIN LEFT AREA --- */}
      <div className="flex-1 flex flex-col h-full overflow-hidden relative min-w-0">

        {/* Top Header */}
        <header className="h-16 md:h-24 px-4 md:px-8 flex items-center justify-between bg-white/50 backdrop-blur-md border-b border-slate-200/50 z-10 sticky top-0 gap-3">
          <div className="flex items-center gap-3 md:gap-6 min-w-0">
            <img
              src="/taplo-mark.png"
              alt="Taplo"
              className="w-10 h-10 md:w-12 md:h-12 flex-shrink-0 select-none"
              draggable={false}
            />
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h1 className="text-lg md:text-2xl font-semibold text-slate-900 tracking-tight truncate">{organization?.displayName || organization?.name || 'TaploPOS'}</h1>
                {organization?.stripeMode === 'test' && (
                  <span className="px-2 py-0.5 text-[10px] md:text-[11px] font-bold uppercase tracking-widest rounded-full bg-amber-100 text-amber-700 border border-amber-200 whitespace-nowrap">
                    Test Mode
                  </span>
                )}
              </div>
              <p className="hidden md:block text-sm text-slate-500 font-medium">{currentDate} • {currentTime}</p>
            </div>
          </div>

          {/* Desktop search bar */}
          <div className="hidden md:flex flex-1 max-w-xl mx-8 relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              placeholder="Search products..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full h-14 pl-12 pr-6 bg-white border border-slate-200 rounded-full text-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-[#2563eb]/20 focus:border-[#2563eb] transition-all"
            />
          </div>

          <div className="flex items-center gap-2 md:gap-4 flex-shrink-0">
            {canAccessSettings && (
              <button
                onClick={() => navigate('/settings/store')}
                className="hidden md:flex w-14 h-14 bg-white rounded-full items-center justify-center shadow-sm border border-slate-200 hover:bg-slate-50 active:scale-95 transition-all"
                title="Settings"
              >
                <Settings className="w-6 h-6 text-slate-600" />
              </button>
            )}

            <button className="hidden md:flex w-14 h-14 bg-white rounded-full items-center justify-center shadow-sm border border-slate-200 hover:bg-slate-50 active:scale-95 transition-all relative">
              <Bell className="w-6 h-6 text-slate-600" />
              <span className="absolute top-3 right-3 w-2.5 h-2.5 bg-red-500 rounded-full border-2 border-white"></span>
            </button>
            <div className="relative" ref={userMenuRef}>
              <button
                onClick={() => setUserMenuOpen(o => !o)}
                className="flex items-center gap-2 md:gap-3 bg-white p-1 md:pl-2 md:pr-5 md:py-2 rounded-full shadow-sm border border-slate-200 hover:bg-slate-50 active:scale-95 transition-all cursor-pointer"
              >
                <div className="w-9 h-9 md:w-10 md:h-10 bg-blue-100 rounded-full flex items-center justify-center text-blue-800 font-bold text-xs md:text-sm tracking-tight">
                  {initials}
                </div>
                <span className="hidden md:inline font-medium text-slate-700">{displayName}</span>
              </button>

              {userMenuOpen && (
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-2xl shadow-xl border border-slate-200 py-2 z-30">
                  <div className="px-4 py-2 border-b border-slate-100">
                    <p className="text-sm font-semibold text-slate-800 truncate">{user?.fullName}</p>
                    <p className="text-xs text-slate-500 truncate">{user?.email}</p>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-red-500 font-semibold text-sm hover:bg-red-50 transition-colors text-left"
                  >
                    <LogOut className="w-4 h-4" />
                    Log Out
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Mobile search bar */}
        <div className="md:hidden px-4 pt-3 pb-1 flex-shrink-0">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search products..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full h-11 pl-11 pr-4 bg-white border border-slate-200 rounded-full text-base shadow-sm focus:outline-none focus:ring-2 focus:ring-[#2563eb]/20 focus:border-[#2563eb] transition-all"
            />
          </div>
        </div>

        {/* Categories (Scrollable Pill Nav) */}
        <div className="pt-3 md:pt-6 pb-2 px-4 md:px-8 overflow-x-auto no-scrollbar flex-shrink-0">
          <div className="flex items-center gap-2 md:gap-3">
            {categories.map(category => {
              const Icon = category.icon;
              const isActive = activeCategory === category.id;
              return (
                <button
                  key={category.id}
                  onClick={() => setActiveCategory(category.id)}
                  className={`
                    flex items-center gap-2 md:gap-3 h-11 md:h-14 px-4 md:px-6 rounded-xl md:rounded-2xl whitespace-nowrap text-sm md:text-lg font-medium transition-all active:scale-95 shadow-sm flex-shrink-0
                    ${isActive
                      ? 'bg-[#2563eb] text-white'
                      : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50 hover:text-slate-900'}
                  `}
                >
                  <Icon className={`w-4 h-4 md:w-5 md:h-5 ${isActive ? 'text-blue-200' : 'text-slate-400'}`} />
                  {category.name}
                </button>
              )
            })}
          </div>
        </div>

        {/* Error handling message */}
        {error && (
          <div className="px-4 md:px-8 mt-2">
             <div className="flex items-center justify-between px-4 py-3 bg-red-50 border border-red-100 text-red-600 rounded-xl text-sm font-medium">
               <span>{error}</span>
               <button onClick={() => setError('')} className="text-red-400 hover:text-red-700 font-bold text-lg leading-none cursor-pointer">&times;</button>
             </div>
          </div>
        )}

        {/* Product Grid */}
        <main className="flex-1 overflow-y-auto px-4 md:px-8 pb-28 md:pb-8 pt-3 md:pt-4 no-scrollbar">
          <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-6">
            {filteredProducts.map(product => (
              <button
                key={product.id}
                onClick={(e) => { e.stopPropagation(); cart.addItem(product); }}
                className="group flex flex-col bg-white rounded-2xl md:rounded-[2rem] p-2 md:p-3 shadow-sm border border-slate-200/60 hover:shadow-md active:scale-[0.98] transition-all text-left relative overflow-hidden"
              >
                <div className="w-full aspect-square rounded-xl md:rounded-[1.5rem] overflow-hidden bg-slate-100 mb-2 md:mb-4 relative flex items-center justify-center">
                  {product.image_url ? (
                    <img
                      src={product.image_url}
                      alt={product.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 absolute inset-0"
                      draggable={false}
                    />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center text-5xl font-bold text-[#2563eb] opacity-10 bg-slate-200/50">
                      {product.name.charAt(0)}
                    </div>
                  )}
                </div>
                <div className="px-1 md:px-3 flex-1 flex flex-col min-h-0">
                  <h3 className="text-base md:text-xl font-semibold text-slate-800 leading-tight mb-1 line-clamp-2">{product.name}</h3>
                  <p className="text-slate-400 font-medium text-xs md:text-sm mt-auto capitalize truncate">{product.category}</p>
                  <div className="flex items-center justify-between mt-1 md:mt-2">
                    <span className="text-base md:text-xl font-bold text-[#2563eb]">{formatCurrency(product.price)}</span>
                    <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-slate-50 flex items-center justify-center text-slate-400 group-hover:bg-[#2563eb] group-hover:text-white transition-colors">
                      <Plus className="w-4 h-4 md:w-5 md:h-5" />
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>

          {filteredProducts.length === 0 && (
            <div className="w-full h-64 flex flex-col items-center justify-center text-slate-400">
              <Search className="w-12 h-12 mb-4 opacity-20" />
              <p className="text-xl font-medium">No products found</p>
            </div>
          )}
        </main>

      </div>

      {/* --- RIGHT SIDEBAR (CART) - desktop only --- */}
      <aside className="hidden md:flex w-[420px] bg-white shadow-2xl flex-col z-20 border-l border-slate-200 flex-shrink-0 relative">

        {/* System Bar (Mock) */}
        <div className="h-8 flex items-center justify-end px-4 gap-3 text-slate-400 text-xs font-medium bg-slate-50">
          <Wifi className="w-4 h-4" />
          <BatteryFull className="w-4 h-4" />
        </div>

        {/* Order Header */}
        <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between bg-white">
          <div>
            <h2 className="text-2xl font-bold text-slate-900 tracking-tight">Current Order</h2>
            <p className="text-sm font-medium text-slate-500 mt-0.5">Terminal Checkout</p>
          </div>
          {cart.items.length > 0 && (
            <button
              onClick={cart.clearCart}
              className="w-12 h-12 rounded-full flex items-center justify-center text-red-400 hover:bg-red-50 active:scale-95 transition-all"
            >
              <Trash2 className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Cart Items */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6 no-scrollbar bg-slate-50/30">
          {cartItemsList}
        </div>

        {/* Totals & Checkout */}
        <div className="p-6 bg-white border-t border-slate-100 shadow-[0_-10px_40px_-15px_rgba(0,0,0,0.05)]">
          {totalsAndCheckout}
        </div>

      </aside>

      {/* --- MOBILE: Floating "View Cart" pill (only on mobile, only when items present) --- */}
      {totalItems > 0 && !mobileCartOpen && (
        <button
          onClick={() => setMobileCartOpen(true)}
          className="md:hidden fixed bottom-4 left-4 right-4 z-30 h-16 bg-[#2563eb] text-white rounded-2xl shadow-2xl shadow-blue-900/30 flex items-center justify-between px-5 active:scale-[0.98] transition-all"
        >
          <div className="flex items-center gap-3">
            <div className="relative w-10 h-10 bg-white/15 rounded-full flex items-center justify-center">
              <ShoppingCart className="w-5 h-5" />
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-blue-300 text-[#1e40af] text-xs font-bold rounded-full flex items-center justify-center border-2 border-[#2563eb]">
                {totalItems}
              </span>
            </div>
            <span className="font-semibold text-base">View Cart</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="font-bold text-lg">{formatCurrency(cart.totals.total)}</span>
            <ChevronRight className="w-5 h-5 opacity-80" />
          </div>
        </button>
      )}

      {/* --- MOBILE: Full-screen cart drawer --- */}
      {mobileCartOpen && (
        <div className="md:hidden fixed inset-0 z-40 flex flex-col bg-white animate-in slide-in-from-bottom duration-200">
          {/* Drawer Header */}
          <div className="px-4 py-4 border-b border-slate-100 flex items-center justify-between bg-white flex-shrink-0">
            <button
              onClick={() => setMobileCartOpen(false)}
              className="flex items-center gap-1 text-[#2563eb] font-medium active:scale-95 transition-all"
            >
              <ChevronLeft className="w-5 h-5" />
              <span>Back</span>
            </button>
            <div className="text-center">
              <h2 className="text-lg font-bold text-slate-900 tracking-tight">Current Order</h2>
              <p className="text-xs font-medium text-slate-500">Terminal Checkout</p>
            </div>
            {cart.items.length > 0 ? (
              <button
                onClick={cart.clearCart}
                className="w-10 h-10 rounded-full flex items-center justify-center text-red-400 hover:bg-red-50 active:scale-95 transition-all"
              >
                <Trash2 className="w-5 h-5" />
              </button>
            ) : (
              <div className="w-10" />
            )}
          </div>

          {/* Error inside mobile drawer */}
          {error && (
            <div className="px-4 pt-3">
              <div className="flex items-center justify-between px-4 py-3 bg-red-50 border border-red-100 text-red-600 rounded-xl text-sm font-medium">
                <span>{error}</span>
                <button onClick={() => setError('')} className="text-red-400 hover:text-red-700 font-bold text-lg leading-none cursor-pointer">&times;</button>
              </div>
            </div>
          )}

          {/* Cart items */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50/30">
            {cartItemsList}
          </div>

          {/* Totals & checkout */}
          <div className="p-4 bg-white border-t border-slate-100 shadow-[0_-10px_40px_-15px_rgba(0,0,0,0.05)] flex-shrink-0">
            {totalsAndCheckout}
          </div>
        </div>
      )}

      {showQrPayment && (
        <QrPayment
          items={cart.items}
          subtotal={cart.totals.subtotal}
          taxAmount={cart.totals.tax}
          convenienceFee={cart.totals.convenienceFee}
          tipAmount={cart.tip}
          total={cart.totals.total}
          onSuccess={handleQrPaymentSuccess}
          onClose={() => setShowQrPayment(false)}
        />
      )}
    </div>
  );
}
